/**
 * The web app. Matches-first by default, not Chat — Chat was demoted from hero to a
 * secondary primary surface (Ahmed, 2026-09-22: "the chatbox is not the best option for
 * interaction"). Superseded the original build spec's "chat-first" framing.
 *
 * Vanilla TS and direct DOM calls, matching the extension and the backend — the project
 * has taken no web-framework dependency anywhere, and three list-shaped views plus a chat
 * view does not justify introducing one.
 *
 * All user- and server-supplied text goes into the DOM through `textContent`, never
 * `innerHTML`. Job descriptions, company names and reasons all originate from scraped
 * pages and model output, so treating any of it as markup would be an injection path
 * straight from a job posting into the user's session.
 *
 * IA (updated 2026-09-22, superseding TARIQ-AI-DESIGN-SPEC-2026-09-18.md's two-surface
 * version): three primary surfaces — Matches (pre-submission review/approval), Applications
 * (post-submission stage tracking, added today), Chat (secondary). Profile (structured
 * fields + resume) and Rules still sit behind the "You" menu, not as top-level tabs.
 */

import { api, ApiError, loginUrl, type ApplicationStage, type ApplicationTransition, type CandidateSummary, type DepthTier, type ProfileSummary, type SayResponse } from './api.ts';
import { atsStatusLabel, buildResumeProfilePatch, type ConfirmedResumeFields, droppedNote, formatCountdown, huntNeedsRules, onboardingSteps, secondsLeft, signinNotice, starterRules } from './session.ts';
import { presentationFor } from './states.ts';

type ViewName = 'chat' | 'matches' | 'applications' | 'profile' | 'rules' | 'pair';
type PrimaryView = 'chat' | 'matches' | 'applications';

// Application stages — cached after first load
let applicationStages: ApplicationStage[] = [];

const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`Missing element #${id}`);
  return node as T;
};

const history: Array<{ role: 'user' | 'assistant'; content: string }> = [];

// A phone-vs-desktop check the mockup and the spec both use for placeholder text and the
// depth caption's one-time reveal. Matches the CSS breakpoint in app.css exactly.
const PHONE = window.matchMedia('(max-width: 639px)');

// ---------------------------------------------------------------------------
// Chrome
// ---------------------------------------------------------------------------

let lastPrimaryView: PrimaryView = 'matches';

function showView(view: ViewName): void {
  for (const section of document.querySelectorAll<HTMLElement>('.view')) {
    section.classList.toggle('active', section.id === `view-${view}`);
  }
  const isPrimary = view === 'chat' || view === 'matches' || view === 'applications';
  if (isPrimary) lastPrimaryView = view;
  // Primary nav (top tabs + bottom tab bar) only ever marks Chat/Matches/Applications active — a
  // secondary view (Profile, Rules) leaves no tab selected, per §2.3.
  for (const tab of document.querySelectorAll<HTMLButtonElement>('.tab, #tabbar button')) {
    if (isPrimary && tab.dataset.view === view) tab.setAttribute('aria-current', 'page');
    else tab.removeAttribute('aria-current');
  }
  closeYouMenu();
  if (view === 'matches') {
    void loadRoles();
    void loadOnboarding();
  } else if (view === 'applications') {
    void loadApplications();
  } else if (view === 'profile') {
    void loadProfile();
    void loadAtsSupport();
  } else if (view === 'rules') {
    void loadRules();
  }
}

/** The You menu's back link returns to whichever primary tab was active before it opened. */
function backFromSecondary(): void {
  showView(lastPrimaryView);
}

function toast(text: string): void {
  const node = el('toast');
  node.textContent = text;
  node.classList.add('show');
  window.setTimeout(() => node.classList.remove('show'), 3200);
}

// ---------------------------------------------------------------------------
// You menu
// ---------------------------------------------------------------------------

function openYouMenu(): void {
  el('you-menu').classList.remove('hidden');
  el('scrim').classList.remove('hidden');
  el('you-btn').setAttribute('aria-expanded', 'true');
}
function closeYouMenu(): void {
  el('you-menu').classList.add('hidden');
  el('scrim').classList.add('hidden');
  el('you-btn').setAttribute('aria-expanded', 'false');
}
function toggleYouMenu(): void {
  if (el('you-menu').classList.contains('hidden')) openYouMenu();
  else closeYouMenu();
}

// ---------------------------------------------------------------------------
// Chat
// ---------------------------------------------------------------------------

/** Tariq's signature — a bronze disc + the name in Newsreader (§2.5, §4.2). Never an avatar. */
function buildAgentMessage(text: string): HTMLElement {
  const node = document.createElement('div');
  node.className = 'msg agent';

  const who = document.createElement('div');
  who.className = 'who';
  who.textContent = 'Tariq';
  node.append(who);

  const body = document.createElement('div');
  body.className = 'body';
  body.textContent = text;
  node.append(body);

  return node;
}

function addMessage(role: 'user' | 'agent' | 'error', text: string): HTMLElement {
  let node: HTMLElement;
  if (role === 'agent') {
    node = buildAgentMessage(text);
  } else {
    node = document.createElement('div');
    node.className = `msg ${role}`;
    node.textContent = role === 'error' ? `Couldn't finish — ${text}` : text;
  }
  el('transcript').append(node);
  node.scrollIntoView({ block: 'end' });
  return node;
}

/**
 * A run is rendered as visible work with a state, never as a blocking spinner (§4.4): the
 * pending state pulses and stays open; the finished state collapses on a clean run and stays
 * open on a failure, because a failure is the one thing the user must not have to click to see.
 */
function addRun(acknowledgement: string): { finish: (result: SayResponse) => void } {
  const node = document.createElement('details');
  node.className = 'run pending';
  node.open = true;

  const summary = document.createElement('summary');
  const dot = document.createElement('span');
  dot.className = 'run-dot';
  const title = document.createElement('span');
  title.className = 'run-title';
  title.textContent = acknowledgement;
  const count = document.createElement('span');
  count.className = 'run-count';
  const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  chevron.setAttribute('class', 'run-chevron');
  chevron.setAttribute('viewBox', '0 0 12 12');
  chevron.setAttribute('fill', 'none');
  chevron.setAttribute('stroke', 'currentColor');
  chevron.setAttribute('stroke-width', '1.5');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M4 2l4 4-4 4');
  chevron.append(path);
  summary.append(dot, title, count, chevron);
  node.append(summary);

  const steps = document.createElement('ol');
  steps.className = 'run-steps';
  node.append(steps);

  el('transcript').append(node);
  node.scrollIntoView({ block: 'end' });

  return {
    finish(result) {
      node.classList.remove('pending');
      steps.textContent = '';

      if (result.results.length === 0) {
        node.remove();
        return;
      }

      const failedCount = result.results.filter((step) => !step.ok).length;
      const allOk = failedCount === 0;
      node.classList.add(allOk ? 'ok' : 'failed');
      node.open = !allOk;

      title.textContent = acknowledgement !== '' ? acknowledgement : 'Done';
      count.textContent = allOk ? `${result.results.length} steps` : `${result.results.length} steps · ${failedCount} failed`;

      for (const step of result.results) {
        const li = document.createElement('li');
        li.className = step.ok ? 'step ok' : 'step failed';
        li.textContent = step.purpose;
        if (!step.ok) {
          const why = document.createElement('span');
          why.className = 'why';
          why.textContent = ` — ${String(step.output)}`;
          li.append(why);
        }
        steps.append(li);
      }
    },
  };
}

function removeSuggestionChips(): void {
  document.querySelector('.chips')?.remove();
}

/** §4.7 — three starting prompts, replaced by a profile nudge when the profile looks empty. */
function renderSuggestionChips(greeting: HTMLElement, profileLooksEmpty: boolean): void {
  const chips = document.createElement('div');
  chips.className = 'chips';

  const firstChip = document.createElement('button');
  firstChip.type = 'button';
  firstChip.className = 'chip';
  if (profileLooksEmpty) {
    firstChip.textContent = 'Set up my profile';
    firstChip.addEventListener('click', () => showView('profile'));
  } else {
    firstChip.textContent = 'Hunt for roles now';
    firstChip.addEventListener('click', () => fillComposer(firstChip.textContent ?? ''));
  }
  chips.append(firstChip);

  for (const text of ['Show me what you set aside, and why', 'Check my resume against a posting']) {
    const chip = document.createElement('button');
    chip.type = 'button';
    chip.className = 'chip';
    chip.textContent = text;
    chip.addEventListener('click', () => fillComposer(text));
    chips.append(chip);
  }
  greeting.append(chips);
}

function fillComposer(text: string): void {
  const input = el<HTMLTextAreaElement>('message');
  input.value = text;
  input.dispatchEvent(new Event('input'));
  input.focus();
}

function autoGrow(textarea: HTMLTextAreaElement): void {
  textarea.style.height = 'auto';
  textarea.style.height = `${textarea.scrollHeight}px`;
}

async function send(): Promise<void> {
  const input = el<HTMLTextAreaElement>('message');
  const message = input.value.trim();
  if (message === '') return;

  removeSuggestionChips();

  const button = el<HTMLButtonElement>('send');
  input.value = '';
  autoGrow(input);
  button.disabled = true;
  const sendLabel = button.querySelector('.label');
  if (sendLabel) sendLabel.textContent = 'Working…';
  addMessage('user', message);
  history.push({ role: 'user', content: message });

  const pending = addRun('Working…');
  try {
    const tier = (document.querySelector<HTMLInputElement>('input[name="tier"]:checked')?.value ?? 'deep') as DepthTier;
    const result = await api.say(message, history.slice(0, -1), tier);
    pending.finish(result);
    addMessage('agent', result.reply);
    history.push({ role: 'assistant', content: result.reply });
    // A turn that ran a hunt has changed the roles list underneath the user.
    if (result.plan.some((step) => step.tool === 'search_jobs')) void loadRoles();
    void refreshAttentionStrip();
  } catch (error) {
    pending.finish({ acknowledgement: '', plan: [], results: [], reply: '' });
    addMessage('error', error instanceof Error ? error.message : String(error));
  } finally {
    button.disabled = false;
    if (sendLabel) sendLabel.textContent = 'Send';
    input.focus();
  }
}

// ---------------------------------------------------------------------------
// Attention strip (§4.9) — the entire dashboard the chat surface is allowed.
// ---------------------------------------------------------------------------

let attentionDismissed = false;

async function refreshAttentionStrip(): Promise<void> {
  const strip = el('attention-strip');
  if (attentionDismissed) {
    strip.classList.add('hidden');
    return;
  }
  try {
    const { candidates } = await api.candidates(['awaiting_approval', 'awaiting_human_submit']);
    if (candidates.length === 0) {
      strip.classList.add('hidden');
      return;
    }
    el('attention-text').textContent =
      candidates.length === 1
        ? '1 application is waiting for your approval.'
        : `${candidates.length} applications are waiting for your approval.`;
    strip.classList.remove('hidden');
  } catch {
    // Non-fatal: the strip is a convenience, not the source of truth — Matches still shows
    // the real state.
    strip.classList.add('hidden');
  }
}

// ---------------------------------------------------------------------------
// Depth tier — the one-line consequence caption (§4.6)
// ---------------------------------------------------------------------------

const DEPTH_CAPTIONS: Record<DepthTier, string> = {
  deep: 'Deep — reads the posting.',
  deeper: 'Deeper — also researches the company. Takes longer.',
  deepest: 'Deepest — also drafts answers. Takes longer still.',
};

let depthHintShownOnPhone = false;

function showDepthCaption(tier: DepthTier): void {
  if (PHONE.matches) {
    if (depthHintShownOnPhone) return;
    depthHintShownOnPhone = true;
    const hint = el('depth-hint');
    hint.textContent = DEPTH_CAPTIONS[tier];
    hint.classList.remove('hidden');
  } else {
    el('composer-hint').textContent = DEPTH_CAPTIONS[tier];
  }
}
function restoreComposerHint(): void {
  if (!PHONE.matches) el('composer-hint').textContent = 'Enter to send · Shift+Enter for a new line';
}

// ---------------------------------------------------------------------------
// Profile (You › Profile) — structured fields plus resume, per §5.3.
// ---------------------------------------------------------------------------

interface TokenField {
  get: () => string[];
  set: (values: string[]) => void;
}

/** A bordered field of chips with an inline text input — Enter/comma adds, backspace-on-empty
 * removes the last chip. Used for target roles, locations, and deal-breakers (§5.3). */
function makeTokenField(container: HTMLElement, placeholder: string): TokenField {
  let values: string[] = [];
  const input = document.createElement('input');
  input.type = 'text';
  input.placeholder = placeholder;

  function render(): void {
    container.textContent = '';
    for (const [index, value] of values.entries()) {
      const chip = document.createElement('span');
      chip.className = 'token';
      const label = document.createElement('span');
      label.textContent = value;
      chip.append(label);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Remove ${value}`);
      remove.addEventListener('click', () => {
        values = values.filter((_, i) => i !== index);
        render();
      });
      chip.append(remove);
      container.append(chip);
    }
    container.append(input);
  }

  function addFromInput(): void {
    const raw = input.value.split(',');
    let added = false;
    for (const piece of raw) {
      const trimmed = piece.trim();
      if (trimmed !== '') {
        values.push(trimmed);
        added = true;
      }
    }
    if (added) {
      input.value = '';
      render();
      input.focus();
    }
  }

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addFromInput();
    } else if (event.key === 'Backspace' && input.value === '' && values.length > 0) {
      values = values.slice(0, -1);
      render();
    }
  });
  input.addEventListener('blur', () => {
    if (input.value.trim() !== '') addFromInput();
  });

  render();

  return {
    get: () => values,
    set: (next) => {
      values = [...next];
      render();
    },
  };
}

let targetRolesField: TokenField | undefined;
let locationsField: TokenField | undefined;
let dealBreakersField: TokenField | undefined;

/** Builds the Profile view's field grid once. Idempotent — safe to call again. */
function buildProfileFields(): void {
  const root = el('profile-fields');
  if (root.dataset.built === 'true') return;
  root.dataset.built = 'true';
  root.textContent = '';

  const targetRolesWrap = document.createElement('div');
  targetRolesWrap.className = 'field wide';
  const targetRolesLabel = document.createElement('label');
  targetRolesLabel.textContent = 'Target roles';
  const targetRolesTokens = document.createElement('div');
  targetRolesTokens.className = 'tokens';
  targetRolesWrap.append(targetRolesLabel, targetRolesTokens);
  targetRolesField = makeTokenField(targetRolesTokens, 'e.g. Head of Product, Director of AI');
  root.append(targetRolesWrap);

  const locationsWrap = document.createElement('div');
  locationsWrap.className = 'field wide';
  const locationsLabel = document.createElement('label');
  locationsLabel.textContent = 'Locations';
  const locationsTokens = document.createElement('div');
  locationsTokens.className = 'tokens';
  locationsWrap.append(locationsLabel, locationsTokens);
  locationsField = makeTokenField(locationsTokens, 'e.g. Cairo, Remote');
  root.append(locationsWrap);

  const salaryWrap = document.createElement('div');
  salaryWrap.className = 'field';
  const salaryLabel = document.createElement('label');
  salaryLabel.setAttribute('for', 'profile-salary-floor');
  salaryLabel.textContent = 'Salary floor';
  const money = document.createElement('div');
  money.className = 'money';
  const salaryInput = document.createElement('input');
  salaryInput.type = 'text';
  salaryInput.inputMode = 'numeric';
  salaryInput.id = 'profile-salary-floor';
  salaryInput.placeholder = 'e.g. 140,000';
  const currencySelect = document.createElement('select');
  currencySelect.id = 'profile-salary-currency';
  for (const code of ['EGP', 'USD', 'EUR', 'GBP', 'AED', 'SAR']) {
    const option = document.createElement('option');
    option.value = code;
    option.textContent = code;
    currencySelect.append(option);
  }
  money.append(salaryInput, currencySelect);
  salaryWrap.append(salaryLabel, money);
  root.append(salaryWrap);

  const authWrap = document.createElement('div');
  authWrap.className = 'field';
  const authLabel = document.createElement('label');
  authLabel.setAttribute('for', 'profile-work-authorization');
  authLabel.textContent = 'Work authorization';
  const authInput = document.createElement('input');
  authInput.type = 'text';
  authInput.id = 'profile-work-authorization';
  const authHelp = document.createElement('div');
  authHelp.className = 'help';
  authHelp.textContent = 'In your own words — e.g. "Egyptian citizen, EU visa needs sponsorship"';
  authWrap.append(authLabel, authInput, authHelp);
  root.append(authWrap);

  const dealBreakersWrap = document.createElement('div');
  dealBreakersWrap.className = 'field wide';
  const dealBreakersLabel = document.createElement('label');
  dealBreakersLabel.textContent = 'Deal-breakers';
  const dealBreakersTokens = document.createElement('div');
  dealBreakersTokens.className = 'tokens';
  const dealBreakersHelp = document.createElement('div');
  dealBreakersHelp.className = 'help';
  dealBreakersHelp.textContent = "Hard no's. Tariq treats these as rules, not preferences.";
  dealBreakersWrap.append(dealBreakersLabel, dealBreakersTokens, dealBreakersHelp);
  dealBreakersField = makeTokenField(dealBreakersTokens, "Add one…");
  root.append(dealBreakersWrap);
}

let lastLoadedProfile: ProfileSummary | undefined;

function profileLooksEmpty(profile: ProfileSummary): boolean {
  return profile.targetRoles.length === 0 && profile.salaryFloor === null;
}

async function loadProfile(): Promise<void> {
  buildProfileFields();
  try {
    const profile = await api.getProfile();
    lastLoadedProfile = profile;
    targetRolesField?.set(profile.targetRoles);
    locationsField?.set(profile.locations);
    dealBreakersField?.set(profile.dealBreakers);
    el<HTMLInputElement>('profile-salary-floor').value = profile.salaryFloor === null ? '' : String(profile.salaryFloor);
    el<HTMLSelectElement>('profile-salary-currency').value = profile.salaryCurrency ?? 'EGP';
    el<HTMLInputElement>('profile-work-authorization').value = profile.workAuthorization ?? '';
  } catch (error) {
    toast(error instanceof Error ? error.message : String(error));
  }
}

async function saveProfile(): Promise<void> {
  const targetRoles = targetRolesField?.get() ?? [];
  if (targetRoles.length === 0) {
    toast('Enter at least one target role.');
    return;
  }

  const salaryFloorRaw = el<HTMLInputElement>('profile-salary-floor').value.replace(/,/g, '').trim();
  const salaryFloor = salaryFloorRaw === '' ? null : Number(salaryFloorRaw);
  if (salaryFloor !== null && (!Number.isFinite(salaryFloor) || salaryFloor <= 0)) {
    toast('Salary floor must be a positive number, or left blank.');
    return;
  }

  const currency = el<HTMLSelectElement>('profile-salary-currency').value.trim();
  const workAuthorization = el<HTMLInputElement>('profile-work-authorization').value.trim();

  try {
    // targetRoles goes through the dedicated route (REPLACES the whole list); everything
    // else is a narrower PATCH /profile.
    await api.saveTargetRoles(targetRoles);
    await api.patchProfile({
      salaryFloor,
      salaryCurrency: salaryFloor === null ? null : currency === '' ? null : currency,
      workAuthorization: workAuthorization === '' ? null : workAuthorization,
      locations: locationsField?.get() ?? [],
      dealBreakers: dealBreakersField?.get() ?? [],
    });
    el('profile-unsaved').classList.add('hidden');
    toast('Profile saved.');
    void refreshAttentionStrip();
  } catch (error) {
    toast(error instanceof Error ? error.message : String(error));
  }
}

async function saveResume(): Promise<void> {
  const label = el<HTMLInputElement>('resume-label').value.trim();
  const content = el<HTMLTextAreaElement>('resume').value.trim();
  if (label === '' || content === '') {
    toast('A resume needs both a label and its text.');
    return;
  }
  try {
    await api.saveResume(label, content);
    toast('Saved and accepted as your current resume.');
  } catch (error) {
    toast(error instanceof Error ? error.message : String(error));
  }
}

// ---------------------------------------------------------------------------
// Matches
// ---------------------------------------------------------------------------

/** States that mean "surfaced for you"; everything else was set aside or is in flight. */
const SET_ASIDE_STATES = new Set(['watch', 'dismissed']);

/** attention-tone states sort first, then progress, then neutral, then success (§5.1). */
const SORT_RANK: Record<Presentation['tone'], number> = { attention: 0, progress: 1, neutral: 2, success: 3 };
type Presentation = ReturnType<typeof presentationFor>;

function renderRole(candidate: CandidateSummary): HTMLElement {
  const card = document.createElement('div');
  card.className = SET_ASIDE_STATES.has(candidate.state) ? 'card set-aside' : 'card';

  const top = document.createElement('div');
  top.className = 'card-top';

  const heading = document.createElement('div');
  const title = document.createElement('h3');
  title.textContent = candidate.title;
  heading.append(title);
  if (candidate.company !== null) {
    const company = document.createElement('div');
    company.className = 'company';
    company.textContent = candidate.company;
    heading.append(company);
  }
  top.append(heading);

  const score = document.createElement('span');
  score.className = 'score';
  score.textContent = candidate.fitScore === null ? '— fit' : `${Math.round(candidate.fitScore * 100)}% fit`;
  top.append(score);
  card.append(top);

  if (candidate.stateReason !== null && candidate.stateReason !== '') {
    const reason = document.createElement('p');
    reason.className = 'reason';
    reason.textContent = candidate.stateReason;
    card.append(reason);
  }

  const meta = document.createElement('div');
  meta.className = 'meta';

  const presentation = presentationFor(candidate.state);
  const badge = document.createElement('span');
  badge.className = `badge ${presentation.tone}`;
  badge.textContent = presentation.label;
  meta.append(badge);

  // Whether a rule or a judgement call set this aside is the difference between "my
  // filters are too strict" and "the agent read it wrong" — two different fixes.
  if (candidate.decidedBy === 'hard_filter') {
    const who = document.createElement('span');
    who.className = 'badge';
    who.textContent = 'one of your hard rules';
    meta.append(who);
  }

  const link = document.createElement('a');
  link.href = candidate.url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = 'Open posting';
  meta.append(link);

  card.append(meta);

  // The approval act. `awaiting_approval` carried the label "Needs your approval" long before
  // there was any way to give it — the state was reachable on screen and nowhere else. This
  // button is the other half of that sentence.
  if (candidate.state === 'awaiting_approval') {
    card.append(renderApproval(candidate));
  }

  return card;
}

/**
 * Approval is shown as a decision, not a confirmation: the wording says what will happen and
 * to whom, because the thing being consented to is an application going out under the user's
 * real name. An "OK" button on a card that says "ready" is not consent, it is a formality.
 */
function renderApproval(candidate: CandidateSummary): HTMLElement {
  const row = document.createElement('div');
  row.className = 'approval';

  if (candidate.approvedAt !== null) {
    const done = document.createElement('span');
    done.className = 'badge success';
    done.textContent = `Approved — waiting to be sent`;
    row.append(done);
    return row;
  }

  const note = document.createElement('p');
  note.textContent =
    'Open the posting and read the filled form before you approve — approving lets Tariq send this application as you.';
  row.append(note);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'primary';
  button.textContent = 'Approve this application';
  button.addEventListener('click', () => {
    button.disabled = true;
    button.textContent = 'Approving…';
    api
      .approve(candidate.candidateId)
      .then(
        () => {
          void loadRoles();
          void refreshAttentionStrip();
        },
        (error: unknown) => {
          // Re-enable rather than swallow: a failed approval that looks approved is the one
          // error shape this surface must never produce.
          button.disabled = false;
          button.textContent = 'Approve this application';
          const failed = document.createElement('p');
          failed.className = 'reason';
          failed.textContent = `Could not approve: ${error instanceof Error ? error.message : String(error)}`;
          row.append(failed);
        },
      );
  });
  row.append(button);
  return row;
}

async function loadRoles(): Promise<void> {
  const list = el('roles-list');
  const includeSetAside = el<HTMLInputElement>('show-set-aside').checked;
  list.textContent = '';

  try {
    const { candidates } = await api.candidates();
    const visible = includeSetAside ? candidates : candidates.filter((c) => !SET_ASIDE_STATES.has(c.state));
    visible.sort((a, b) => {
      const rank = SORT_RANK[presentationFor(a.state).tone] - SORT_RANK[presentationFor(b.state).tone];
      if (rank !== 0) return rank;
      return Date.parse(b.createdAt) - Date.parse(a.createdAt);
    });

    if (visible.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'empty';
      empty.textContent = includeSetAside
        ? 'Nothing yet. Run a hunt to see what is out there.'
        : 'Nothing surfaced yet. Tick "show what was set aside" to see what was filtered out.';
      list.append(empty);
      return;
    }
    for (const candidate of visible) list.append(renderRole(candidate));
  } catch (error) {
    const failed = document.createElement('p');
    failed.className = 'empty';
    failed.textContent = error instanceof Error ? error.message : String(error);
    list.append(failed);
  }
}

// ---------------------------------------------------------------------------
// Applications (submitted candidates with stage tracking)
// ---------------------------------------------------------------------------

/**
 * Render a submitted candidate card with current stage and transition history.
 */
function renderApplication(candidate: CandidateSummary): HTMLElement {
  const card = document.createElement('div');
  card.className = 'card';

  const top = document.createElement('div');
  top.className = 'card-top';

  const heading = document.createElement('div');
  const title = document.createElement('h3');
  title.textContent = candidate.title;
  heading.append(title);
  if (candidate.company !== null) {
    const company = document.createElement('div');
    company.className = 'company';
    company.textContent = candidate.company;
    heading.append(company);
  }
  top.append(heading);

  const score = document.createElement('span');
  score.className = 'score';
  score.textContent = candidate.fitScore === null ? '— fit' : `${Math.round(candidate.fitScore * 100)}% fit`;
  top.append(score);
  card.append(top);

  const meta = document.createElement('div');
  meta.className = 'meta';

  const presentation = presentationFor(candidate.state);
  const badge = document.createElement('span');
  badge.className = `badge ${presentation.tone}`;
  badge.textContent = presentation.label;
  meta.append(badge);

  const link = document.createElement('a');
  link.href = candidate.url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.textContent = 'Open posting';
  meta.append(link);

  card.append(meta);

  // Show application stage tracking for submitted applications
  card.append(renderApplicationStage(candidate));

  return card;
}

async function loadApplications(): Promise<void> {
  const list = el('applications-list');
  list.textContent = '';

  try {
    const { candidates } = await api.candidates(['submitted']);
    if (candidates.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'empty';
      empty.textContent = 'No applications submitted yet.';
      list.append(empty);
      return;
    }
    for (const candidate of candidates) {
      list.append(renderApplication(candidate));
    }
  } catch (error) {
    const failed = document.createElement('p');
    failed.className = 'empty';
    failed.textContent = error instanceof Error ? error.message : String(error);
    list.append(failed);
  }
}

// ---------------------------------------------------------------------------
// Application Stage Tracking (post-submission pipeline)
// ---------------------------------------------------------------------------

/**
 * Ensures applicationStages is loaded, caching the result.
 */
async function ensureStagesLoaded(): Promise<void> {
  if (applicationStages.length === 0) {
    try {
      const response = await api.getApplicationStages();
      applicationStages = response.stages;
    } catch (error) {
      console.error('Failed to load application stages:', error);
      throw error;
    }
  }
}

/**
 * Render the stage section for a submitted candidate. Shows current stage and transition
 * history, with a dropdown to move to another stage.
 */
function renderApplicationStage(candidate: CandidateSummary): HTMLElement {
  const section = document.createElement('div');
  section.className = 'application-stage';

  const header = document.createElement('p');
  header.className = 'stage-header';
  header.textContent = 'Application Pipeline';
  section.append(header);

  // Show current stage and allow stage changes
  const stageControl = document.createElement('div');
  stageControl.className = 'stage-control';

  const label = document.createElement('label');
  label.textContent = 'Current stage: ';

  const select = document.createElement('select');
  select.className = 'stage-select';
  select.disabled = true; // Will be enabled after loading current state

  const loading = document.createElement('option');
  loading.value = '';
  loading.textContent = 'Loading...';
  select.append(loading);

  label.append(select);
  stageControl.append(label);
  section.append(stageControl);

  // Load and display current stage + transitions
  void (async () => {
    try {
      await ensureStagesLoaded();
      const { transitions, currentStage } = await api.getApplicationTransitions(candidate.candidateId);

      // Update current stage display
      select.textContent = '';
      if (currentStage) {
        const current = document.createElement('option');
        current.value = currentStage.id;
        current.textContent = currentStage.name;
        current.selected = true;
        select.append(current);
      } else {
        const noStage = document.createElement('option');
        noStage.value = '';
        noStage.textContent = 'Not yet moved';
        noStage.selected = true;
        select.append(noStage);
      }

      // Add available stages
      for (const stage of applicationStages) {
        if (!currentStage || stage.id !== currentStage.id) {
          const option = document.createElement('option');
          option.value = stage.id;
          option.textContent = stage.name;
          select.append(option);
        }
      }

      select.disabled = false;

      // Handle stage changes
      select.addEventListener('change', () => {
        if (select.value && select.value !== (currentStage?.id ?? '')) {
          void handleMoveStage(candidate.candidateId, select.value, select);
        }
      });

      // Show transition history
      if (transitions.length > 0) {
        const history = document.createElement('div');
        history.className = 'stage-history';

        const historyTitle = document.createElement('p');
        historyTitle.className = 'history-title';
        historyTitle.textContent = 'Stage History';
        history.append(historyTitle);

        const list = document.createElement('ul');
        list.className = 'history-list';

        for (const transition of transitions) {
          const item = document.createElement('li');
          item.className = 'history-item';

          const stageName = applicationStages.find((s) => s.id === transition.newStageId)?.name ?? 'Unknown';
          const actor = transition.actorType === 'human' ? 'You' : 'System';
          const date = new Date(transition.createdAt).toLocaleString();

          let text = `${stageName} — ${actor} on ${date}`;
          if (transition.reasoning) {
            text += ` (${transition.reasoning})`;
          }

          item.textContent = text;
          list.append(item);
        }

        history.append(list);
        section.append(history);
      }
    } catch (error) {
      const errorMsg = document.createElement('p');
      errorMsg.className = 'error';
      errorMsg.textContent = `Could not load stage info: ${error instanceof Error ? error.message : String(error)}`;
      section.append(errorMsg);
    }
  })();

  return section;
}

/**
 * Handle a stage change for a candidate.
 */
async function handleMoveStage(candidateId: string, newStageId: string, select: HTMLSelectElement): Promise<void> {
  const oldValue = select.value;
  select.disabled = true;

  try {
    await api.moveApplicationStage(candidateId, newStageId);
    toast('Stage updated');
    // Reload the roles list to refresh the stage display
    void loadRoles();
  } catch (error) {
    // Revert the selection
    select.value = oldValue;
    const msg = error instanceof Error ? error.message : String(error);
    toast(`Could not move stage: ${msg}`);
  } finally {
    select.disabled = false;
  }
}

// ---------------------------------------------------------------------------
// Activity (§2.4) — one ambient, expandable line at the top of Matches. There is no
// history endpoint exposed over HTTP today (`runsRepo.listRuns` is backend-internal), so
// this tracks hunts run in the current browser session only — real, just not durable
// across a reload. That is a deliberate, documented scope cut, not a bug.
// ---------------------------------------------------------------------------

interface ActivityEntry {
  at: number;
  read: number;
  setAside: number;
  surfaced: number;
}
const activityLog: ActivityEntry[] = [];

function relativeTime(at: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function formatActivity(entry: ActivityEntry): string {
  return `${relativeTime(entry.at)} · read ${entry.read} · set aside ${entry.setAside} by your rules · surfaced ${entry.surfaced}`;
}

function renderActivity(): void {
  const summary = el('activity-summary');
  const list = el('activity-list');
  list.textContent = '';

  if (activityLog.length === 0) {
    summary.textContent = 'No hunts yet.';
    return;
  }
  const [latest, ...rest] = activityLog;
  if (latest === undefined) {
    summary.textContent = 'No hunts yet.';
    return;
  }
  summary.textContent = `Last hunt ${formatActivity(latest)}`;
  for (const entry of rest.slice(0, 4)) {
    const li = document.createElement('li');
    li.textContent = formatActivity(entry);
    list.append(li);
  }
}

async function runHunt(): Promise<void> {
  const button = el<HTMLButtonElement>('run-hunt');
  button.disabled = true;
  button.textContent = 'Hunting…';
  try {
    const result = await api.hunt();
    await loadRoles();
    const skipped = result.skipped.length;
    activityLog.unshift({
      at: Date.now(),
      read: result.shortlisted,
      setAside: result.shortlisted - result.judged + skipped,
      surfaced: result.surfaced.length,
    });
    renderActivity();
    toast(
      `Read ${result.shortlisted}, judged ${result.judged}, surfaced ${result.surfaced.length}.` +
        (skipped > 0 ? ` ${skipped} skipped.` : ''),
    );
    void loadOnboarding();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (huntNeedsRules(message)) {
      toast('Write your rules first — Tariq needs them before it can hunt.');
      showView('rules');
    } else {
      toast(message);
    }
  } finally {
    button.disabled = false;
    button.textContent = 'Run a hunt';
  }
}

// ---------------------------------------------------------------------------
// Where Tariq can apply (P2-6 support matrix)
// ---------------------------------------------------------------------------

let atsSupportLoaded = false;

async function loadAtsSupport(): Promise<void> {
  if (atsSupportLoaded) return; // static content — fetch once per session, not on every profile visit
  try {
    const { entries } = await api.getAtsSupport();
    const list = el('ats-support-list');
    list.replaceChildren(
      ...entries.map((entry) => {
        const { text, tone } = atsStatusLabel(entry.status);
        const li = document.createElement('li');
        const badge = document.createElement('span');
        badge.className = `ats-badge ats-badge-${tone}`;
        badge.textContent = text;
        const name = document.createElement('span');
        name.className = 'ats-name';
        name.textContent = entry.platform;
        li.append(name, badge);
        li.title = entry.note;
        return li;
      }),
    );
    atsSupportLoaded = true;
  } catch {
    // Non-fatal: the list is informational, not blocking.
  }
}

// ---------------------------------------------------------------------------
// Getting started card
// ---------------------------------------------------------------------------

async function loadOnboarding(): Promise<void> {
  const card = el('onboarding');
  try {
    const steps = onboardingSteps(await api.getOnboarding());
    if (steps === null) {
      card.classList.add('hidden');
      return;
    }
    const list = el('onboarding-steps');
    list.replaceChildren(
      ...steps.map((step) => {
        const li = document.createElement('li');
        li.classList.toggle('done', step.done);
        li.append(`${step.done ? '✓' : '○'} ${step.label}`);
        if (!step.done) {
          const go = document.createElement('button');
          go.type = 'button';
          go.textContent = step.key === 'hunt' ? 'Run a hunt' : 'Go';
          go.addEventListener('click', () => (step.key === 'hunt' ? void runHunt() : showView(step.view)));
          li.append(go);
        }
        return li;
      }),
    );
    card.classList.remove('hidden');
  } catch {
    // Non-fatal: the card is a convenience; hide it rather than show stale steps.
    card.classList.add('hidden');
  }
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

async function fillStarterRules(): Promise<void> {
  const box = el<HTMLTextAreaElement>('rules');
  if (box.value.trim() !== '' && !confirm('Replace what is in the box with a starter built from your profile?')) return;
  try {
    box.value = starterRules(await api.getProfile());
    box.dispatchEvent(new Event('input'));
  } catch (error) {
    toast(error instanceof Error ? error.message : String(error));
  }
}

let lastSavedRules = '';

async function loadRules(): Promise<void> {
  try {
    const { content } = await api.getRules();
    lastSavedRules = content ?? '';
    el<HTMLTextAreaElement>('rules').value = lastSavedRules;
    el('rules-unsaved').classList.add('hidden');
  } catch {
    // Non-fatal: an empty editor is a usable starting state, and the save path will
    // report its own error if the backend is genuinely unreachable.
  }
}

async function saveRules(): Promise<void> {
  const content = el<HTMLTextAreaElement>('rules').value.trim();
  if (content === '') {
    toast('Your rules cannot be empty — Tariq reads this before every decision.');
    return;
  }
  try {
    await api.saveRules(content);
    lastSavedRules = content;
    el('rules-unsaved').classList.add('hidden');
    toast('Rules saved.');
  } catch (error) {
    toast(error instanceof Error ? error.message : String(error));
  }
}

// ---------------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------------

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-view]') : null;
  if (target !== null) {
    showView(target.dataset.view as ViewName);
    return;
  }
  if (event.target instanceof Element && event.target.closest('#scrim') !== null) closeYouMenu();
});

el('you-btn').addEventListener('click', toggleYouMenu);
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeYouMenu();
});

for (const back of document.querySelectorAll<HTMLButtonElement>('.back')) {
  back.addEventListener('click', backFromSecondary);
}

el('composer').addEventListener('submit', (event) => {
  event.preventDefault();
  void send();
});

const messageInput = el<HTMLTextAreaElement>('message');
messageInput.addEventListener('input', () => autoGrow(messageInput));
messageInput.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    void send();
  } else if (event.key === 'Escape' && messageInput.value !== '') {
    messageInput.value = '';
    autoGrow(messageInput);
  }
});
{
  const full = messageInput.placeholder;
  const applyPlaceholder = (): void => {
    messageInput.placeholder = PHONE.matches ? (messageInput.dataset.phonePlaceholder ?? full) : full;
  };
  PHONE.addEventListener('change', applyPlaceholder);
  applyPlaceholder();
}

for (const radio of document.querySelectorAll<HTMLInputElement>('input[name="tier"]')) {
  radio.addEventListener('focus', () => showDepthCaption(radio.value as DepthTier));
  radio.addEventListener('mouseenter', () => showDepthCaption(radio.value as DepthTier));
  radio.addEventListener('mouseleave', restoreComposerHint);
  radio.addEventListener('blur', restoreComposerHint);
}

el('attention-dismiss').addEventListener('click', () => {
  attentionDismissed = true;
  el('attention-strip').classList.add('hidden');
});

el('save-profile').addEventListener('click', () => void saveProfile());
el('save-resume').addEventListener('click', () => void saveResume());
el('run-hunt').addEventListener('click', () => void runHunt());
el('show-set-aside').addEventListener('change', () => void loadRoles());
el('save-rules').addEventListener('click', () => void saveRules());
el('starter-rules').addEventListener('click', () => void fillStarterRules());
el<HTMLTextAreaElement>('rules').addEventListener('input', () => {
  el('rules-unsaved').classList.toggle('hidden', el<HTMLTextAreaElement>('rules').value === lastSavedRules);
});

for (const type of ['input', 'change']) {
  el('profile-fields').addEventListener(type, () => el('profile-unsaved').classList.remove('hidden'));
}

renderActivity();

const greeting = addMessage(
  'agent',
  "I hunt for roles worth your time, research them, and get applications ready. You decide what goes out — I never submit anything on my own.\n\nStart by putting your resume and your rules in, then ask me to hunt.",
);

function boot(): void {
  void loadRules();
  void refreshAttentionStrip();
  void api
    .getProfile()
    .then((profile) => {
      lastLoadedProfile = profile;
      renderSuggestionChips(greeting, profileLooksEmpty(profile));
    })
    .catch(() => renderSuggestionChips(greeting, false));
}

// ---------------------------------------------------------------------------
// Resume upload: read the file, pre-fill, confirm, then save
// ---------------------------------------------------------------------------

const RC_FIELDS = ['firstName', 'lastName', 'email', 'phone', 'linkedinUrl', 'githubUrl', 'currentLocation', 'targetRoles'] as const;
let pendingResumeText = '';
let pendingResumeName = '';

function setUploadStatus(text: string | null): void {
  const node = el('resume-upload-status');
  node.textContent = text ?? '';
  node.classList.toggle('hidden', text === null);
}

async function handleResumeFile(file: File): Promise<void> {
  setUploadStatus(`Reading ${file.name}…`);
  el('resume-confirm').classList.add('hidden');
  try {
    const result = await api.uploadResume(file);
    pendingResumeText = result.resumeText;
    pendingResumeName = file.name.replace(/\.[^.]+$/, '');
    for (const field of RC_FIELDS) {
      const value = field === 'targetRoles' ? result.proposed.targetRoles.join(', ') : (result.proposed[field] ?? '');
      el<HTMLInputElement>(`rc-${field}`).value = value;
    }
    const note = droppedNote(result.dropped);
    const noteNode = el('resume-dropped');
    noteNode.textContent = note ?? 'Check these against your resume. Nothing is saved until you confirm.';
    el('resume-confirm').classList.remove('hidden');
    setUploadStatus(null);
  } catch (error) {
    setUploadStatus(error instanceof Error ? error.message : 'Could not read that file.');
  }
}

async function confirmResumeUpload(): Promise<void> {
  const read = (f: (typeof RC_FIELDS)[number]): string => el<HTMLInputElement>(`rc-${f}`).value;
  const values: ConfirmedResumeFields = {
    firstName: read('firstName'),
    lastName: read('lastName'),
    email: read('email'),
    phone: read('phone'),
    linkedinUrl: read('linkedinUrl'),
    githubUrl: read('githubUrl'),
    currentLocation: read('currentLocation'),
    targetRoles: read('targetRoles'),
  };
  const { patch, targetRoles } = buildResumeProfilePatch(values);
  const button = el<HTMLButtonElement>('rc-confirm');
  button.disabled = true;
  try {
    await api.saveResume(pendingResumeName === '' ? 'Uploaded resume' : pendingResumeName, pendingResumeText);
    if (Object.keys(patch).length > 0) await api.patchProfile(patch);
    if (targetRoles.length > 0) await api.saveTargetRoles(targetRoles);
    el<HTMLTextAreaElement>('resume').value = pendingResumeText;
    el('resume-confirm').classList.add('hidden');
    toast('Resume saved and your profile updated.');
    void loadProfile();
  } catch (error) {
    toast(error instanceof Error ? error.message : String(error));
  } finally {
    button.disabled = false;
  }
}

el<HTMLInputElement>('resume-file').addEventListener('change', (event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (file) void handleResumeFile(file);
});
el('rc-confirm').addEventListener('click', () => void confirmResumeUpload());
el('rc-cancel').addEventListener('click', () => el('resume-confirm').classList.add('hidden'));
const drop = el('resume-drop');
drop.addEventListener('dragover', (event) => {
  event.preventDefault();
  drop.classList.add('dragging');
});
drop.addEventListener('dragleave', () => drop.classList.remove('dragging'));
drop.addEventListener('drop', (event) => {
  event.preventDefault();
  drop.classList.remove('dragging');
  const file = event.dataTransfer?.files[0];
  if (file) void handleResumeFile(file);
});

// ---------------------------------------------------------------------------
// Sign-in gate and extension pairing
// ---------------------------------------------------------------------------

let pairTimer: number | undefined;

function renderPairCountdown(expiresAt: string): void {
  window.clearInterval(pairTimer);
  const tick = (): void => {
    const left = secondsLeft(expiresAt, Date.now());
    el('pair-expiry').textContent = left > 0 ? `Expires in ${formatCountdown(left)}` : 'This code has expired — get a new one.';
    if (left === 0) window.clearInterval(pairTimer);
  };
  tick();
  pairTimer = window.setInterval(tick, 1000);
}

async function generatePairingCode(): Promise<void> {
  const button = el<HTMLButtonElement>('pair-generate');
  button.disabled = true;
  try {
    const { code, expiresAt } = await api.startPairing();
    el('pair-code').textContent = code;
    renderPairCountdown(expiresAt);
    button.textContent = 'Get a new code';
  } catch (error) {
    toast(error instanceof Error ? error.message : 'Could not get a pairing code.');
  } finally {
    button.disabled = false;
  }
}

function showSignIn(): void {
  el('app').classList.add('hidden');
  el('signin').classList.remove('hidden');
  el<HTMLAnchorElement>('signin-google').href = loginUrl;
  const notice = signinNotice(window.location.search);
  if (notice !== null) {
    const node = el('signin-notice');
    node.textContent = notice;
    node.classList.remove('hidden');
  }
}

el('pair-generate').addEventListener('click', () => void generatePairingCode());
el('sign-out').addEventListener('click', () => {
  void api
    .logout()
    .catch(() => undefined)
    .then(() => {
      window.location.assign(window.location.pathname);
    });
});

/** The app only boots once the backend confirms a session; a 401 shows the sign-in screen instead. */
async function start(): Promise<void> {
  try {
    const me = await api.me();
    el('you-email').textContent = me.email;
    el('signin').classList.add('hidden');
    el('app').classList.remove('hidden');
    boot();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      showSignIn();
      return;
    }
    // Backend unreachable or erroring: say so rather than showing a sign-in screen that cannot work.
    showSignIn();
    const node = el('signin-notice');
    node.textContent = 'Tariq could not reach the server. Please try again in a moment.';
    node.classList.remove('hidden');
  }
}

void start();
